/* eslint-disable */
import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  Paper,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Avatar,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Assignment,
  AutoFixHigh,
  Edit,
  Download,
  FormatPaint,
  Visibility,
  Verified,
  Language,
  Lightbulb,
  ArrowForward,
  Check,
  EmojiEvents,
  Speed,
  TrendingUp,
  Bolt,
  ExpandMore,
  PlayArrow,
  Security,
  CloudDone,
  Star,
  People,
  AccessTime,
  School,
  Business,
  Psychology,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

/**
 * Enhanced Responsive Home Page
 * – Improved accessibility and performance
 * – Added FAQ section and social proof
 * – Better conversion optimization
 * – Enhanced mobile experience
 */

const tokens = {
  colours: {
    primary: '#4953F3',
    secondary: '#7C3AED',
    accent: '#06B6D4',
    success: '#22C55E',
    warning: '#F59E0B',
    textDark: '#0F172A',
    textLight: '#F8FAFC',
    bgLight: '#F1F5F9',
    gradient: 'linear-gradient(135deg,#4953F3 0%,#7C3AED 100%)',
    accentGradient: 'linear-gradient(135deg,#06B6D4 0%,#0EA5E9 100%)',
  },
  radius: 4,
  shadowLg: '0 18px 40px rgba(0,0,0,0.12)',
  shadowMd: '0 8px 25px rgba(0,0,0,0.08)',
};

const steps = [
  {
    icon: <Assignment fontSize="inherit" />,
    title: 'Input Details',
    description: 'Add your professional information & skills. Speak to us, no forms!',
    time: '2 min',
  },
  {
    icon: <AutoFixHigh fontSize="inherit" />,
    title: 'AI Analysis',
    description: 'We generate an ATS-optimised resume with AI.',
    time: '30 sec',
  },
  {
    icon: <Edit fontSize="inherit" />,
    title: 'Customise',
    description: 'Download your resume and adjust as needed.',
    time: '5 min',
  },
  {
    icon: <Download fontSize="inherit" />,
    title: 'Export',
    description: 'Get PDF, DOCX & web versions.',
    time: 'Instant',
  },
];

const features = [
  {
    icon: <AutoFixHigh fontSize="inherit" />,
    title: 'AI Optimisation',
    description: 'Highlight achievements with smart prompts.',
    badge: 'Popular',
  },
  {
    icon: <FormatPaint fontSize="inherit" />,
    title: 'Pro Templates',
    description: 'Pixel-perfect designs for every industry.',
    badge: null,
  },
  {
    icon: <Visibility fontSize="inherit" />,
    title: 'Easy to use',
    description: 'Just use your words, we get you!',
    badge: 'New',
  },
  {
    icon: <Verified fontSize="inherit" />,
    title: 'ATS Friendly',
    description: 'Structured to breeze past screeners.',
    badge: 'Essential',
  },
  {
    icon: <Language fontSize="inherit" />,
    title: 'Multilingual',
    description: 'Locale-specific formatting.',
    badge: null,
  },
  {
    icon: <Lightbulb fontSize="inherit" />,
    title: 'Skill Gaps',
    description: 'Receive in-demand skill recommendations.',
    badge: 'AI',
  },
];

const testimonials = [
  {
    name: 'Douglas Martins',
    role: 'Software Engineer',
    quote: 'Three interviews in a week – golden tips!',
    rating: 5,
    image:
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=200&q=80',
  },
  {
    name: 'Anna Smith',
    role: 'Marketing Specialist',
    quote: 'Templates made all the difference.',
    rating: 5,
    image:
      'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=200&q=80',
  },
  {
    name: 'Mike Johnson',
    role: 'Graduate',
    quote: 'AI coach turned my notes into a polished CV.',
    rating: 5,
    image:
      'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=200&q=80',
  },
];

const benefits = [
  'Save hours with automated formatting',
  'Beat bots with ATS-friendly structure',
  'Unlimited revisions & cloud backups',
  'No subscription – buy me a coffee!',
];

const statsHighlights = [
  { icon: <People />, value: '10K+', label: 'Users Trust Us' },
  { icon: <TrendingUp />, value: '85%', label: 'Interview Rate' },
  { icon: <EmojiEvents />, value: '4.9★', label: 'User Rating' },
  { icon: <AccessTime />, value: '<8min', label: 'Average Time' },
];

const trustIndicators = [
  { icon: <Security />, label: 'Bank-level Security' },
  { icon: <CloudDone />, label: 'Cloud Backup' },
  { icon: <Verified />, label: 'Privacy Protected' },
];

const industryTemplates = [
  { name: 'Tech', icon: <Psychology />, color: tokens.colours.accent },
  { name: 'Business', icon: <Business />, color: tokens.colours.primary },
  { name: 'Academic', icon: <School />, color: tokens.colours.secondary },
  { name: 'Creative', icon: <FormatPaint />, color: tokens.colours.warning },
];

const faqs = [
  {
    question: 'How does the speech-to-text feature work?',
    answer: 'Simply click record and speak naturally about your experience. Our AI processes your speech and converts it into professional resume content, saving you from tedious form-filling.',
  },
  {
    question: 'Are the resumes really ATS-friendly?',
    answer: 'Absolutely! Our templates are specifically designed to pass Applicant Tracking Systems. We use proper heading structures, standard fonts, and optimized formatting that ATS software can easily parse.',
  },
  {
    question: 'Can I edit the generated resume?',
    answer: 'Yes! After generation, you can download and edit your resume in multiple formats (PDF, DOCX, web). You have full control over the final output.',
  },
  {
    question: 'What makes this different from other resume builders?',
    answer: 'Our unique speech-to-text approach eliminates boring forms. Plus, our AI doesn\'t just format – it actively improves your content with industry-specific suggestions and achievement optimization.',
  },
  {
    question: 'Is my data secure?',
    answer: 'Your privacy is our priority. We use bank-level encryption, don\'t store unnecessary data, and you own all content you create. Read our privacy policy for full details.',
  },
];

const SectionTitle = ({ label, accent = 'secondary', subtitle }) => (
  <Box textAlign="center" mb={{ xs: 6, md: 8 }}>
    <Typography
      variant="h2"
      component="h2"
      sx={{
        fontWeight: 800,
        color: tokens.colours.textDark,
        mb: 2,
        fontSize: {
          xs: 'clamp(1.5rem,5vw,2.25rem)',
          md: 'clamp(2.5rem,4vw,3rem)',
        },
      }}
    >
      {label}
    </Typography>
    {subtitle && (
      <Typography
        variant="h6"
        component="p"
        sx={{
          color: 'text.secondary',
          fontWeight: 400,
          maxWidth: 720,
          mx: 'auto',
        }}
      >
        {subtitle}
      </Typography>
    )}
    <Divider
      sx={{
        width: 60,
        mx: 'auto',
        mt: 3,
        borderColor: tokens.colours[accent],
        borderWidth: 3,
        borderRadius: 2,
      }}
    />
  </Box>
);

const GlassCard = ({ children, sx, ...props }) => (
  <Card
    sx={{
      backdropFilter: 'blur(12px)',
      background: 'rgba(255,255,255,0.6)',
      boxShadow: tokens.shadowLg,
      borderRadius: tokens.radius * 4,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
      },
      ...sx,
    }}
    {...props}
  >
    {children}
  </Card>
);

const HomePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const prefersReducedMotion = useReducedMotion();
  const [expandedFaq, setExpandedFaq] = useState(false);

  const handleFaqChange = useCallback((panel) => (event, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : false);
  }, []);

  /** Motion helpers with reduced motion support */
  const fadeUp = useMemo(() => ({
    hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 30 },
    show: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { 
        delay: prefersReducedMotion ? 0 : i * 0.1, 
        duration: prefersReducedMotion ? 0.2 : 0.6, 
        ease: 'easeOut' 
      },
    }),
  }), [prefersReducedMotion]);

  /** Reduce visual noise on mobile */
  const mobileFeatures = isMobile ? features.slice(0, 3) : features;
  const showTestimonials = !isMobile;
  const showFloatingShapes = !isMobile && !prefersReducedMotion;

  // Memoized floating shapes for performance
  const floatingShapes = useMemo(() => {
    if (!showFloatingShapes) return null;
    
    return [...Array(10)].map((_, i) => (
      <motion.div
        key={i}
        initial={{
          x: `${Math.random() * 100}%`,
          y: `${Math.random() * 100}%`,
          rotate: Math.random() * 360,
          opacity: 0.1 + Math.random() * 0.1,
        }}
        animate={{
          y: [`${Math.random() * 80}%`, `${Math.random() * 80}%`],
          x: [`${Math.random() * 20}%`, `${Math.random() * 20}%`],
          rotate: 360,
        }}
        transition={{
          duration: 25 + Math.random() * 10,
          repeat: Infinity,
          ease: 'linear',
        }}
        style={{
          position: 'absolute',
          width: 60 + Math.random() * 40,
          height: 60 + Math.random() * 40,
          borderRadius: `${40 + Math.random() * 20}%`,
          background: 'rgba(255,255,255,0.06)',
          filter: 'blur(6px)',
        }}
      />
    ));
  }, [showFloatingShapes]);

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      {/* Skip Navigation */}
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: 'absolute',
          left: '-9999px',
          zIndex: 999,
          '&:focus': {
            left: '6px',
            top: '6px',
            background: tokens.colours.primary,
            color: 'white',
            padding: '8px',
            textDecoration: 'none',
            borderRadius: '4px',
          },
        }}
      >
        Skip to main content
      </Box>

      {/* ───────────────── Hero ───────────────── */}
      <Box
        component="section"
        aria-label="Hero section"
        sx={{
          position: 'relative',
          minHeight: { xs: '80vh', md: '95vh' },
          display: 'flex',
          alignItems: 'center',
          background: tokens.colours.gradient,
          color: tokens.colours.textLight,
          overflow: 'hidden',
          pt: 4,
          pb: 8,
        }}
      >
        {/* Floating shapes */}
        {showFloatingShapes && (
          <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
            {floatingShapes}
          </Box>
        )}

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid
            container
            spacing={{ xs: 4, md: 8 }}
            alignItems="center"
            direction={{ xs: 'column-reverse', md: 'row' }}
          >
            {/* Text column */}
            <Grid item xs={12} md={6}>
              <motion.div variants={fadeUp} initial="hidden" animate="show">
                <Typography
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    fontSize: {
                      xs: 'clamp(2rem,7vw,2.75rem)',
                      md: 'clamp(3.25rem,5vw,4.25rem)',
                    },
                    lineHeight: 1.15,
                    mb: 3,
                    background: tokens.colours.accentGradient,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    filter: 'drop-shadow(0 2px 10px rgba(6,182,212,0.3))',
                    textAlign: { xs: 'center', md: 'left' },
                  }}
                >
                  Speech-to-text Resume Generator
                </Typography>
                <Typography
                  variant="h5"
                  component="p"
                  sx={{
                    fontWeight: 400,
                    color: 'rgba(255,255,255,0.9)',
                    mb: 3,
                    maxWidth: 540,
                    lineHeight: 1.4,
                    mx: { xs: 'auto', md: 0 },
                    textAlign: { xs: 'center', md: 'left' },
                  }}
                >
                  cleverCV pairs advanced AI with ATS-ready templates to help you create professional resumes. <Box component="span" sx={{ fontWeight: 700, color: tokens.colours.accent }}>Just talk!</Box>
                </Typography>

                {/* Trust indicators */}
                <Stack 
                  direction="row" 
                  spacing={3} 
                  sx={{ 
                    mb: 4, 
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    flexWrap: 'wrap',
                  }}
                >
                  {trustIndicators.map((indicator) => (
                    <Box
                      key={indicator.label}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: 'rgba(255,255,255,0.9)',
                        fontSize: '0.875rem',
                      }}
                    >
                      <Box sx={{ mr: 0.5, color: tokens.colours.success }}>
                        {indicator.icon}
                      </Box>
                      {indicator.label}
                    </Box>
                  ))}
                </Stack>

                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    mb: 5,
                    justifyContent: { xs: 'center', md: 'flex-start' },
                  }}
                >
                  <Button
                    component={Link}
                    to="/generate"
                    variant="contained"
                    size="large"
                    startIcon={<PlayArrow />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 3,
                      backgroundColor: tokens.colours.accent,
                      boxShadow: '0 10px 25px rgba(6,182,212,0.4)',
                      '&:hover': {
                        backgroundColor: tokens.colours.accent,
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Try it now!
                  </Button>
                  <Button
                    href="#how-it-works"
                    variant="outlined"
                    size="large"
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 3,
                      borderWidth: 2,
                      color: '#fff',
                      borderColor: 'rgba(255,255,255,0.6)',
                      '&:hover': {
                        borderColor: '#fff',
                        background: 'rgba(255,255,255,0.1)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Learn More
                  </Button>
                </Box>

                {/* Enhanced Stats Row */}
                {!isMobile && (
                  <Grid container spacing={2}>
                    {statsHighlights.map((stat, i) => (
                      <Grid item xs={6} key={stat.label}>
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.6 + i * 0.1 }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              p: 1.5,
                              borderRadius: 2,
                              background: 'rgba(255,255,255,0.08)',
                              backdropFilter: 'blur(4px)',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                mr: 1.5,
                                backgroundColor: `${tokens.colours.accent}40`,
                                color: tokens.colours.accent,
                              }}
                            >
                              {stat.icon}
                            </Box>
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                {stat.value}
                              </Typography>
                              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                {stat.label}
                              </Typography>
                            </Box>
                          </Box>
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </motion.div>
            </Grid>

            {/* Enhanced CV Mock-up */}
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7 }}
                style={{ perspective: 1200, width: '100%' }}
              >
                <motion.div
                  whileHover={!isMobile && !prefersReducedMotion ? { rotateY: 8, rotateX: -3 } : {}}
                  transition={{ type: 'spring', stiffness: 100 }}
                  style={{ position: 'relative', width: '100%' }}
                >
                  <Paper
                    elevation={12}
                    sx={{
                      position: 'relative',
                      width: '100%',
                      maxWidth: { xs: '300px', md: '85%' },
                      mx: 'auto',
                      borderRadius: 4,
                      overflow: 'hidden',
                      boxShadow: tokens.shadowLg,
                      '&:before': !isMobile && {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '40%',
                        background: 'linear-gradient(to bottom, rgba(6,182,212,0.1), transparent)',
                        zIndex: 2,
                        pointerEvents: 'none',
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src="/CV.png"
                      alt="Professional ATS-optimized resume template preview showing clean layout and proper formatting"
                      sx={{ width: '100%', height: 'auto', display: 'block' }}
                    />

                    {/* Enhanced ATS Badge */}
                    {!isMobile && (
                      <Tooltip title="ATS-Compatible Design" arrow>
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 20,
                            right: 20,
                            background: 'rgba(255,255,255,0.95)',
                            boxShadow: tokens.shadowMd,
                            borderRadius: '50%',
                            width: 60,
                            height: 60,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 3,
                            border: `2px solid ${tokens.colours.success}`,
                            cursor: 'pointer',
                          }}
                        >
                          <motion.div 
                            animate={prefersReducedMotion ? {} : { rotate: 360 }} 
                            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                          >
                            <Verified sx={{ fontSize: 36, color: tokens.colours.success }} />
                          </motion.div>
                        </Box>
                      </Tooltip>
                    )}
                  </Paper>
                </motion.div>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ───────────────── Industry Templates Preview ───────────────── */}
      <Box 
        component="section" 
        aria-label="Industry templates"
        sx={{ py: { xs: 6, md: 8 }, backgroundColor: tokens.colours.bgLight }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center" mb={4}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.secondary', mb: 2 }}>
              Trusted by professionals in
            </Typography>
            <Stack 
              direction="row" 
              spacing={3} 
              justifyContent="center" 
              flexWrap="wrap"
              sx={{ gap: 2 }}
            >
              {industryTemplates.map((industry) => (
                <Chip
                  key={industry.name}
                  icon={industry.icon}
                  label={industry.name}
                  sx={{
                    px: 2,
                    py: 1,
                    fontWeight: 600,
                    backgroundColor: `${industry.color}15`,
                    color: industry.color,
                    border: `1px solid ${industry.color}30`,
                    '&:hover': {
                      backgroundColor: `${industry.color}25`,
                    },
                  }}
                />
              ))}
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* ───────────────── Enhanced Steps ───────────────── */}
      <Box 
        id="how-it-works" 
        component="section"
        aria-label="How it works"
        sx={{ py: { xs: 8, md: 14 }, backgroundColor: 'white' }}
      >
        <Container maxWidth="lg">
          <SectionTitle label="How It Works" subtitle="Four simple steps to your perfect resume." />
          <Grid container spacing={4}>
            {steps.map((s, i) => (
              <Grid key={s.title} item xs={12} sm={6} md={3}>
                <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}>
                  <GlassCard
                    sx={{
                      textAlign: 'center',
                      p: 4,
                      position: 'relative',
                      overflow: 'visible',
                    }}
                  >
                    {/* Step number */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -15,
                        left: 20,
                        backgroundColor: tokens.colours.primary,
                        color: 'white',
                        borderRadius: '50%',
                        width: 30,
                        height: 30,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.875rem',
                        fontWeight: 700,
                      }}
                    >
                      {i + 1}
                    </Box>

                    {/* Time estimate */}
                    <Chip
                      label={s.time}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 15,
                        right: 15,
                        backgroundColor: `${tokens.colours.accent}15`,
                        color: tokens.colours.accent,
                        fontWeight: 600,
                      }}
                    />

                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        mb: 3,
                        backgroundColor: `${tokens.colours.primary}20`,
                        color: tokens.colours.primary,
                        fontSize: 48,
                      }}
                    >
                      {s.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      {s.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {s.description}
                    </Typography>
                  </GlassCard>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ───────────────── Enhanced Features ───────────────── */}
      <Box 
        component="section"
        aria-label="Features"
        sx={{ py: { xs: 8, md: 14 }, backgroundColor: tokens.colours.bgLight }}
      >
        <Container maxWidth="lg">
          <SectionTitle label="Features" subtitle="Built for today's competitive job market" accent="accent" />
          <Grid container spacing={4}>
            {mobileFeatures.map((f, i) => (
              <Grid key={f.title} item xs={12} sm={6} md={4}>
                <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}>
                  <GlassCard sx={{ p: 4, height: '100%', position: 'relative' }}>
                    {f.badge && (
                      <Chip
                        label={f.badge}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 15,
                          right: 15,
                          backgroundColor: tokens.colours.warning,
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, fontSize: 36, color: tokens.colours.accent }}>
                      {f.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      {f.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {f.description}
                    </Typography>
                  </GlassCard>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ───────────────── Enhanced Testimonials ───────────────── */}
      {showTestimonials && (
        <Box 
          component="section"
          aria-label="User testimonials"
          sx={{ py: 14, backgroundColor: 'white' }}
        >
          <Container maxWidth="lg">
            <SectionTitle label="Success Stories" subtitle="Join thousands who landed their dream jobs" accent="success" />
            <Grid container spacing={4}>
              {testimonials.map((t, i) => (
                <Grid key={t.name} item xs={12} md={4}>
                  <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}>
                    <GlassCard sx={{ p: 4, height: '100%' }}>
                      {/* Star rating */}
                      <Box sx={{ display: 'flex', mb: 2 }}>
                        {[...Array(t.rating)].map((_, starIndex) => (
                          <Star key={starIndex} sx={{ color: tokens.colours.warning, fontSize: 20 }} />
                        ))}
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar
                          src={t.image}
                          alt={`${t.name}'s profile picture`}
                          sx={{
                            width: 60,
                            height: 60,
                            mr: 2,
                            border: `2px solid ${tokens.colours.accent}`,
                          }}
                        />
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {t.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t.role}
                          </Typography>
                          <Typography variant="caption" sx={{ color: tokens.colours.primary, fontWeight: 600 }}>
                            {t.company}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        "{t.quote}"
                      </Typography>
                    </GlassCard>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* ───────────────── FAQ Section ───────────────── */}
      <Box 
        component="section"
        aria-label="Frequently asked questions"
        sx={{ py: { xs: 8, md: 14 }, backgroundColor: tokens.colours.bgLight }}
      >
        <Container maxWidth="md">
          <SectionTitle label="FAQ" subtitle="Everything you need to know" />
          <Box>
            {faqs.map((faq, index) => (
              <Accordion
                key={index}
                expanded={expandedFaq === `panel${index}`}
                onChange={handleFaqChange(`panel${index}`)}
                sx={{
                  mb: 2,
                  boxShadow: tokens.shadowMd,
                  borderRadius: 2,
                  '&:before': { display: 'none' },
                  '&.MuiAccordion-root.Mui-expanded': {
                    margin: '0 0 16px 0',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    borderRadius: 2,
                    '&.Mui-expanded': {
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                    },
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ───────────────── Enhanced CTA ───────────────── */}
      <Box 
        component="section"
        aria-label="Call to action"
        sx={{ py: { xs: 8, md: 14 }, background: tokens.colours.gradient, color: '#fff' }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center" direction={{ xs: 'column', md: 'row' }}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  fontWeight: 800,
                  mb: 4,
                  fontSize: {
                    xs: 'clamp(1.75rem,4vw,2.25rem)',
                    md: 'clamp(2.25rem,3vw,3rem)',
                  },
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                Ready to build your perfect CV?
              </Typography>
              <Box sx={{ mb: 4 }}>
                {benefits.map((b) => (
                  <Box key={b} sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                    <Check sx={{ mr: 1, color: tokens.colours.accent }} />
                    <Typography variant="body1">{b}</Typography>
                  </Box>
                ))}
              </Box>

              {/* Social proof */}
              <Box 
                sx={{ 
                  mb: 4, 
                  p: 3, 
                  background: 'rgba(255,255,255,0.1)', 
                  borderRadius: 2,
                  textAlign: { xs: 'center', md: 'left' } 
                }}
              >
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  Join 10,000+ professionals who've already upgraded their careers
                </Typography>
                <Stack direction="row" spacing={1} justifyContent={{ xs: 'center', md: 'flex-start' }}>
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} sx={{ color: tokens.colours.warning, fontSize: 20 }} />
                  ))}
                  <Typography variant="body2" sx={{ ml: 1, fontWeight: 600 }}>
                    4.9/5 average rating
                  </Typography>
                </Stack>
              </Box>

              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Button
                  component={Link}
                  to="/generate"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 3,
                    backgroundColor: '#fff',
                    color: tokens.colours.primary,
                    boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                    '&:hover': { 
                      backgroundColor: '#fff',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Start Creating Now
                </Button>
              </Box>
            </Grid>
            {/* Empty grid item for potential future content */}
            {!isMobile && <Grid item xs={12} md={6}></Grid>}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
